"use client";

import { usePathname } from "next/navigation";
import { useImpersonation } from "@/app/hooks/useImpersonation";
import { Button } from "@/components/ui/button";
import { AlertTriangle, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export function ImpersonationBanner() {
  const pathname = usePathname();
  const { isImpersonating, impersonatedUser, originalAdmin, stopImpersonation, isLoading } =
    useImpersonation();
  const { toast } = useToast();

  // Hide on login page – avoids stale impersonation state when not authenticated
  const isLoginPage = pathname === "/" || pathname === "/login";

  if (isLoginPage || !isImpersonating || !impersonatedUser) {
    return null;
  }

  const handleStopImpersonation = async () => {
    try {
      await stopImpersonation();
      // Don't show toast here as redirect happens immediately
      // Redirect is handled in stopImpersonation hook
    } catch (error) {
      console.error("Error stopping impersonation:", error);
      toast({
        title: "Error",
        description: "Failed to stop impersonation. Please try again.",
        variant: "destructive",
      });
    }
  };

  const impersonatedUserName = impersonatedUser.first_name && impersonatedUser.last_name
    ? `${impersonatedUser.first_name} ${impersonatedUser.last_name}`
    : impersonatedUser.email || "Unknown User";

  const originalAdminName = originalAdmin?.first_name && originalAdmin?.last_name
    ? `${originalAdmin.first_name} ${originalAdmin.last_name}`
    : originalAdmin?.email || "staff user";

  return (
    <div className="bg-yellow-500 border-b-2 border-yellow-600 text-yellow-900 px-4 py-3 shadow-md z-50 relative">
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-sm">
              You are impersonating{" "}
              <span className="font-bold">
                {impersonatedUserName} ({impersonatedUser.email})
              </span>
            </p>
            <p className="text-xs mt-0.5">
              Original user: {originalAdminName}
            </p>
          </div>
        </div>
        <Button
          onClick={handleStopImpersonation}
          disabled={isLoading}
          variant="outline"
          size="sm"
          className="bg-white hover:bg-yellow-50 border-yellow-600 text-yellow-900 font-semibold ml-4"
        >
          <X className="h-4 w-4 mr-2" />
          Stop Impersonation
        </Button>
      </div>
    </div>
  );
}

